import type { Maybe } from 'graphql/jsutils/Maybe';
import type { ReactElement } from 'react';

import { memo } from 'react';
import styled from 'styled-components';

import { HybrisCmsLink } from '../../shared/core/link/HybrisCmsLink';
import { colors } from '../../shared/core/styles';
import type { NavigationItem } from '~/utilities/graphql/codegen';

const S = {
  NextChildrenWrapper: styled.div<{ $isOpen: boolean; $height?: number }>`
    height: ${({ $isOpen, $height }) => ($isOpen && $height ? $height : 0)}px;
    overflow: hidden;
    transition: height 0.5s cubic-bezier(0.35, 0, 0.25, 1);
    will-change: height;
  `,

  LinkList: styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
  `,

  NextChildLink: styled(HybrisCmsLink)`
    display: block;
    color: ${colors.ACCESSIBILITY_GREY};
    padding: 8px 0;
    text-align: center;

    li:last-child > & {
      padding-bottom: 32px;
    }

    &:hover,
    &:focus {
      color: ${({ theme }) => theme.colors.content.component.buttons.primary.rest};
    }
  `,
};

type NextChildrenProps = {
  node: Maybe<NavigationItem>;
  i: number;
  j: number;
  isOpen: boolean;
  height: number;
  isParentOpen: boolean;
  onRef: (el: HTMLDivElement) => void;
};

const NextChildrenComponent = ({
  node,
  i,
  j,
  isOpen,
  height,
  isParentOpen,
  onRef,
}: NextChildrenProps): ReactElement => (
  <S.NextChildrenWrapper
    $isOpen={isOpen}
    id={`inpage-accordion-panel-${i}-${j}`}
    aria-labelledby={`inpage-accordion-btn-${i}-${j}`}
    aria-hidden={!isOpen}
    ref={(el: HTMLDivElement) => {
      if (el) {
        onRef(el);
      }
    }}
    $height={height}
  >
    <S.LinkList>
      {node?.children?.map(
        link =>
          link?.url && (
            <li key={link.uid}>
              <S.NextChildLink
                target={link.target}
                href={link.url}
                tabIndex={!isParentOpen || !isOpen ? -1 : 0}
              >
                {link?.label}
              </S.NextChildLink>
            </li>
          )
      )}
    </S.LinkList>
  </S.NextChildrenWrapper>
);

export const NextChildren = memo(NextChildrenComponent);
